<template>
  <basic-dialog :show="show" @close="$emit('close')" max-width="1000px">
    <template v-slot:title>{{ title }}</template>
    <v-row align="baseline">
      <v-col>
        <v-text-field
          label="Route Name"
          v-model="name"
          :outlined="outlinedInputs"
          hide-details
        ></v-text-field>
      </v-col>
      <v-col sm="2">
        <v-row justify="center">
          <v-switch
            label="Edit Path"
            v-model="editRoute"
            hide-details
            :disabled="!editEnabled"
          ></v-switch>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <GmapMap
        ref="gmap"
        :center="center"
        :zoom="zoom"
        @click="onMapClick"
        style="width: 100%; height: 400px"
        @bounds_changed="onMapBoundsChanged"
        @zoom_changed="onZoomChanged"
      >
        <gmap-polyline
          v-if="isNew"
          :path="routeCoordinates"
          ref="polyline"
          :editable="editRoute"
        ></gmap-polyline>
        <gmap-polyline
          v-else
          :path="editablePath"
          ref="polyline"
          :editable="editRoute"
          @path_changed="updatePath"
        ></gmap-polyline>

        <gmap-marker
          v-for="poi of poisWithIcons"
          :key="poi.id"
          :position="{ lng: Number(poi.lng), lat: Number(poi.lat) }"
          :label="poi.name"
          :icon="poi.icon"
        ></gmap-marker>
      </GmapMap>
    </v-row>
    <v-row v-if="false">
      <v-col>
        <code
          >Points: {{ path.length }}| zoom: {{ zoom }} | editablePath:
          {{ editablePath }}</code
        >
      </v-col>
      <v-col>
        <v-slider
          v-model="tolerance"
          class="align-center"
          :max="0.001"
          :min="0"
          hide-details
          :label="`Simplification: ${tolerance}`"
          step="0.00001"
        ></v-slider>
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="!hideDescription" cols="8">
        <v-textarea
          label="Description"
          v-model="description"
          :outlined="outlinedInputs"
          hide-details
          rows="6"
        ></v-textarea>
      </v-col>
      <v-col v-if="showColorPicker" cols="4">
        <v-col v-if="showColorPicker">
          <v-color-picker
            v-model="color"
            mode="hexa"
            hide-canvas
          ></v-color-picker>
        </v-col>
      </v-col>
    </v-row>
    <v-row>
      <v-col v-if="showTags">
        <tags-select
          v-model="tags"
          :outlined="outlinedInputs"
          hide-details
          owner="ROUTE"
          dense
        ></tags-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col v-if="showDate">
        <date-picker
          v-model="date"
          :outlined="outlinedInputs"
          hide-details
          :range-start="rangeStart"
          :range-end="rangeEnd"
        ></date-picker>
      </v-col>

      <v-col v-if="showSequence" cols="2">
        <v-text-field
          label="Sequence"
          v-model.number="sequence"
          :outlined="outlinedInputs"
          hide-details
          type="number"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row v-if="!isNew">
      <v-col>
        <route-pois
          :map-center="center"
          v-model="pois"
          :route-id="routeId"
          :path="path"
          :isSource="isSource"
          @save="onSaveSourceRoutePoi"
          @delete="onDeleteSourceRoutePoi"
        ></route-pois>
      </v-col>
    </v-row>
    <div v-if="debug">
      <code>{{ dateRange }}</code>
    </div>
    <template v-slot:actions>
      <v-btn color="primary" @click="onSave">Save</v-btn>
    </template>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../shared/BasicDialog";
import RoutePois from "./RoutePOIS";

import { removeGQLTypename } from "../../../../helpers";
import simplify from "simplify-js";
import { gmapApi } from "vue2-google-maps";
import DatePicker from "../../shared/DatePicker.vue";
import TagsSelect from "../../shared/TagsSelect.vue";

export default {
  name: "route-details-dialog",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => false,
    },
    isSource: {
      type: Boolean,
      default: () => false,
    },
    sourcePois: {
      type: Array,
      default: () => [],
    },
    hideDescription: {
      type: Boolean,
      default: () => false,
    },
    showTags: {
      type: Boolean,
      default: () => false,
    },
    showDate: {
      type: Boolean,
      default: () => false,
    },
    showColorPicker: {
      type: Boolean,
      default: () => false,
    },
    showSequence: {
      type: Boolean,
      default: () => false,
    },
    dateRange: {
      type: Object,
      default: () => null,
    },
  },

  components: { BasicDialog, RoutePois, DatePicker, TagsSelect },

  data() {
    return {
      debug: false,
      name: this.value ? this.value.name : "",
      description: this.value ? this.value.description : "",
      coordinates: this.value ? this.value.coordinates : "",
      tags: this.value ? this.value.tags : "",
      editRoute: false,
      tolerance: 0.00001,
      zoom: this.value && this.value.id ? 13 : 2,
      editZoom: 15,
      editablePath: [],
      date: this.value ? this.value.date : "",
      color: this.value ? this.value.color : "#FF0000",
      sequence: this.value ? this.value.sequence : 0,
      pois: this.isSource
        ? this.sourcePois
        : this.value && this.value.pois
        ? this.value.pois.edges.map(removeGQLTypename)
        : [],
    };
  },

  computed: {
    rangeStart() {
      return this.dateRange ? this.dateRange.start : null;
    },
    rangeEnd() {
      return this.dateRange ? this.dateRange.end : null;
    },
    poisWithIcons() {
      return this.pois.map((p) => {
        const icon =
          p.type && p.type.iconSVG
            ? `data:image/svg+xml;utf-8, \
         <svg version="1.0" xmlns="http://www.w3.org/2000/svg" width="55.000000pt" height="55.000000pt" viewBox="0 0 100.000000 100.000000" preserveAspectRatio="xMidYMid meet"> \
        <g transform="translate(0.000000,100.000000) scale(0.100000,-0.100000)" fill="%23000000" stroke="none"> \
                <path fill="%233b6bf9" d="${p.type.iconSVG}"/>
        </g> \
        </svg>`
            : `data:image/svg+xml;utf-8, \
          <svg width="30" height="30" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"> \
          <path fill="%233b6bf9" d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"></path> \
          </svg>`;
        //console.log(p);
        return { ...p, icon };
      });
    },
    isNew() {
      return !(this.value && this.value.id);
    },
    polyline() {
      return this.editRoute ? this.simplifiedPath : this.path;
    },
    routeCoordinates() {
      let index = 0;

      return this.show && this.coordinates != ""
        ? this.coordinates
            .replaceAll("\n", " ")
            .split(" ")
            .map((str) => {
              const strArr = str.split(",");
              if (!isNaN(Number(strArr[1]))) {
                //return { lat: Number(strArr[1]), lng: Number(strArr[0]) };
                const p = {
                  lng: Number(strArr[0]),
                  lat: Number(strArr[1]),
                  index,
                };
                index++;
                return p;
              }
            })
            .filter((obj) => obj != undefined)
        : [];
    },
    title() {
      return this.value
        ? `${this.value.name} ${this.value.code ? `(${this.value.code})` : ""}`
        : "Route Details";
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    path() {
      return this.routeCoordinates;
    },
    simplifiedPath() {
      return simplify(
        this.path.map((p) => ({ x: p.lat, y: p.lng })),
        this.tolerance,
        true
      ).map((p) => ({
        lat: p.x,
        lng: p.y,
      }));
    },
    center() {
      return this.routeCoordinates.length > 0
        ? this.routeCoordinates[0]
        : {
            lng: 19.623715499999985,
            lat: -4.29067900091282,
          };
    },
    routeId() {
      return this.value ? this.value.id : "";
    },
    editEnabled() {
      return this.isNew || this.zoom > this.editZoom;
    },
    google() {
      return gmapApi();
    },
  },

  watch: {
    value(newVal) {
      this.pois = newVal.pois ? newVal.pois.edges.map(removeGQLTypename) : [];
      this.name = newVal.name;
      this.description = newVal.description;
      this.coordinates = newVal.coordinates;
      this.zoom = newVal.id ? 14 : 2;
      this.tags = newVal.tags ? newVal.tags : "";
      this.date = newVal.date ? newVal.date : "";
      this.color = newVal.color ? newVal.color : "#FF0000";
      this.sequence = newVal.sequence ? newVal.sequence : 0;
    },
    sourcePois(newVal) {
      this.pois = [...newVal];
    },
  },

  methods: {
    onSave() {
      this.$emit("save", {
        code: this.value.code,
        name: this.name,
        description: this.description,
        coordinates: this.coordinates,
        tags: this.tags ? this.tags : "",
        date: this.date.toString() != "" ? this.date : null,
        color: this.color ? this.color : "#FF0000",
        sequence: this.sequence,
      });
    },
    updatePath(path) {
      const editedCords = [];
      const route = this.getPathCoordinates();

      const editStartIndex = this.editablePath[0].index;
      const editEndIndex =
        this.editablePath[this.editablePath.length - 1].index;

      const firstPart = route.slice(0, editStartIndex);
      const lastPart = route.slice(editEndIndex + 1);

      for (let i = 0; i < path.length; i++) {
        //coords += `${path.getAt(i).lng()},${path.getAt(i).lat()}\n`;
        editedCords.push({
          lat: path.getAt(i).lat(),
          lng: path.getAt(i).lng(),
        });
      }
      //   console.log(
      //     "start: ",
      //     editStartIndex,
      //     firstPart,
      //     firstPart.length,
      //     "middle: ",
      //     editedCords.length,
      //     "end: ",
      //     editEndIndex,
      //     lastPart,
      //     lastPart.length
      //   );
      const newCoords = [...firstPart, ...editedCords, ...lastPart];
      //convert to strin
      this.coordinates = newCoords.reduce(
        (str, c) => (str += `${c.lng},${c.lat}\n`),
        ""
      );
    },
    onMapClick(event) {
      console.log("onMapClick", event);
      console.log(`${event.latLng.lng()},${event.latLng.lat()}\n`);
      if (this.isNew && this.editRoute) {
        this.coordinates += `${event.latLng.lng()},${event.latLng.lat()}\n`;
      }
    },
    onMapBoundsChanged(event) {
      if (event) {
        //console.log(this.routeCoordinates);
        this.editablePath = [
          ...this.routeCoordinates.filter((p) => event.contains(p)),
        ];
      }
    },
    onZoomChanged(event) {
      this.zoom = event;
      if (this.editRoute && this.zoom < this.editZoom && !this.isNew) {
        this.editRoute = false;
      }
    },
    getPathCoordinates() {
      let index = 0;
      return this.coordinates
        .split("\n")
        .map((str) => {
          const strArr = str.split(",");
          if (!isNaN(Number(strArr[1]))) {
            //return { lat: Number(strArr[1]), lng: Number(strArr[0]) };
            const p = {
              lng: Number(strArr[0]),
              lat: Number(strArr[1]),
              index,
            };
            index++;
            return p;
          }
        })
        .filter((obj) => obj != undefined);
    },
    onSaveSourceRoutePoi(payload) {
      this.$emit("save-poi", payload);
    },
    onDeleteSourceRoutePoi(payload) {
      this.$emit("delete-poi", payload);
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/routes/RouteDetailsDialog.vue